import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import useThemeStore from 'Theme/store';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/outline';

const CustomToast = ({ id, message }) => {
  const [isExiting, setIsExiting] = useState(false);
  const { theme: themeValue } = useThemeStore();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
    }, 4500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`${isExiting ? 'toast-exit' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: themeValue === 'dark' ? '#333' : '#fff',
        color: themeValue === 'dark' ? '#fff' : '#333',
        padding: '8px 16px',
        borderRadius: '8px',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        boxShadow:
          themeValue === 'dark'
            ? '0 4px 8px rgba(0, 0, 0, 0.7)'
            : '0 4px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <span>📖 {message}</span>
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => toast.dismiss(id), 500);
        }}
        style={{
          marginLeft: '16px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: themeValue === 'dark' ? '#fff' : '#333',
          fontSize: '12px',
          padding: '0',
          lineHeight: '1',
        }}
      >
        ✖️
      </button>
    </div>
  );
};

const CustomSuccessToast = ({ id, message }) => {
  const [visible, setVisible] = useState(false);
  const { theme: themeValue } = useThemeStore();

  useEffect(() => {
    const enter = setTimeout(() => setVisible(true), 10);
    const autoClose = setTimeout(() => handleClose(), 20000);

    return () => {
      clearTimeout(enter);
      clearTimeout(autoClose);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => toast.dismiss(id), 300);
  };

  const isDark = themeValue === 'dark';
  const backgroundColor = isDark ? '#333' : '#f7f8fa'; // Un gris clarito bonito
  const textColor = isDark ? '#f9fafb' : '#111827';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor,
        padding: '16px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        gap: '16px',
        fontFamily: 'sans-serif',
        maxWidth: '350px',
        width: 'fit-content',
        color: textColor,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-10px)',
        wordWrap: 'break-word',
        whiteSpace: 'normal',
      }}
    >
      <div
        style={{
          backgroundColor: '#22c55e', // verde de éxito
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CheckIcon size={16} color="white" />
      </div>

      <span
        style={{
          flex: 1,
          color: textColor,
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
        }}
      >
        {message}
      </span>

      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          color: textColor,
          fontSize: '16px',
          cursor: 'pointer',
          padding: 0,
        }}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  );
};

const CustomErrorToast = ({ id, message }) => {
  const [visible, setVisible] = useState(false);
  const { theme: themeValue } = useThemeStore();

  useEffect(() => {
    const enter = setTimeout(() => setVisible(true), 10);
    const autoClose = setTimeout(() => handleClose(), 20000);

    return () => {
      clearTimeout(enter);
      clearTimeout(autoClose);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => toast.dismiss(id), 300);
  };

  const isDark = themeValue === 'dark';
  const backgroundColor = isDark ? '#333' : '#f7f8fa';
  const textColor = isDark ? '#f9fafb' : '#111827';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor,
        padding: '16px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        gap: '16px',
        fontFamily: 'sans-serif',
        width: 'fit-content',
        minWidth: '220px',
        maxWidth: '90vw',
        color: textColor,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-10px)',
      }}
    >
      <div
        style={{
          backgroundColor: '#f87171',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <XMarkIcon style={{ width: '16px', height: '16px', color: 'white' }} />
      </div>

      <span style={{ flex: 1, color: textColor }}>{message}</span>

      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          color: textColor,
          fontSize: '16px',
          cursor: 'pointer',
          padding: 0,
        }}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  );
};

const CustomToastIcon = ({ id, message, icon }) => {
  const [visible, setVisible] = useState(false);
  const { theme: themeValue } = useThemeStore();

  useEffect(() => {
    const enter = setTimeout(() => setVisible(true), 10);
    const autoClose = setTimeout(() => handleClose(), 20000); // 20 segundos

    return () => {
      clearTimeout(enter);
      clearTimeout(autoClose);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => toast.dismiss(id), 300);
  };

  const isDark = themeValue === 'dark';
  const backgroundColor = isDark ? '#333' : '#f7f8fa';
  const textColor = isDark ? '#f9fafb' : '#111827';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor,
        padding: '16px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        gap: '16px',
        fontFamily: 'sans-serif',
        maxWidth: '350px',
        width: 'fit-content',
        color: textColor,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-10px)',
        wordWrap: 'break-word',
        whiteSpace: 'normal',
      }}
    >
      {icon && (
        <div
          style={{
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      )}

      <span
        style={{
          flex: 1,
          color: textColor,
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
        }}
      >
        {message}
      </span>

      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          color: textColor,
          fontSize: '16px',
          cursor: 'pointer',
          padding: 0,
        }}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  );
};

export default CustomErrorToast;

export { CustomToastIcon, CustomSuccessToast, CustomErrorToast, CustomToast };
